import React, { useContext } from 'react';
import SummaryContainer from '../../../components/summary/SummaryContainer';
import SummaryTitle from '../../../components/summary/SummaryTitle';
import { Alert, Box, Link as MuiLink, Typography } from '@mui/material';
import SummaryContent from '../../../components/summary/SummaryContent';
import { courseTypeToPresentationNameMap } from '../../../components/cards/MuiCard/CourseMuiCard/utils';
import { palette, spaces } from '../../../assets/styles/themes/tokens';
import { Metadata } from '../../../components/cards/MuiCard/MuiCardBase/MuiCardBase';
import { useTranslation } from 'react-i18next';
import Tooltip from '../../../components/Tooltip';
import useScreenSize from '../../../utils/useScreenSize';
import { CourseCoverPageContext } from './CourseCoverPageProvider';
import CourseCoverButton from './CourseCoverButton';
import { getSubscription, selectUser } from '../../../reducers/user.selectors';
import { useSelector } from 'react-redux';
import { ampliConversionPath, externalLinks, routes, userType } from '../../../constants';
import { Link } from 'found';
import services from '../../../services';

const CourseCoverPageSummary = () => {
  const { t } = useTranslation();
  const { 
    mindItemId, 
    isLoading, 
    courseMindItem, 
    accessLevel
  } = useContext(CourseCoverPageContext);
  const { isMdScreen, isLgScreen } = useScreenSize();
  const isDesktopScreen = isMdScreen || isLgScreen;
  const user = useSelector(selectUser);
  const subscription = useSelector(getSubscription);

  const getAlertText = () => {
    switch (user?.type) {
      case userType.basis: 
        return (
          <>
            {t('Free access does not include any courses. Discover our ')}
            <Link
              to={`/${routes.upgradePackageSelection.root}`}
              onClick={() => {
                services.track.ampliTrackPricingPlansViewedEvent(
                  ampliConversionPath.CourseCoverPage,
                  subscription?.status
                );
              }}
            >
              <Typography 
                variant='body2'
                component='span'
                sx={{ fontWeight: 500, textDecoration: 'underline' }} 
                color={palette.info.contrastText}
              >
                {t('Premium packages.')}
              </Typography>
            </Link>
          </>
        );

      case userType.document:
        if (user?.isSchoolUser) {
          return (
            <>
              {t('No courses are included in your school access.')}{' '}
              <MuiLink 
                href={externalLinks.schoolLicenceOffer}
                target='_blank' 
                rel='noopener noreferrer' 
              >
                <Typography 
                  component='span' 
                  variant='body2' 
                  sx={{ fontWeight: 500, textDecoration: 'underline' }} 
                  color={palette.info.contrastText}
                >
                  {t('Discover')}
                </Typography>
              </MuiLink>
              {t(' our extended school package.')}
            </>
          );
        } else if (user?.isWalkIn) {
          return t('No courses are included in your library access.');
        } else if (user?.isInTrial) {
          return t('Your trial subscription only includes one free course.'
            +' If you keep your subscription, you can start another free course in the next calendar month.');
        } else if (!user?.canUpgrade || user?.isLibraryUser) {
          return t('You can start a new training programme from the next calendar month.');
        } else {
          return (
            <>
              {t('Course limit reached. You can start a new course from the next calendar month. With ')}
              <Link
                to={`/${routes.upgradePackageSelection.root}`}
              >
                <Typography 
                  component='span' 
                  variant='body2' 
                  sx={{ fontWeight: 500, textDecoration: 'underline' }} 
                  color={palette.info.contrastText}
                >
                  {t('meinUnterricht 360')}
                </Typography>
              </Link>
              {t(' you have unlimited access to courses.')}
            </>
          );
        }

      default: 
        return null;
    }

  };

  const alertText = getAlertText();

  return (
    <SummaryContainer>
      {accessLevel === 'none' && alertText && (
        <Alert
          severity='info'
          variant='filled'
          sx={{ backgroundColor: palette.info.main, color: palette.info.contrastText }}
        >
          <Typography
            variant='body2' 
            sx={{ fontWeight: 500 }} 
            color={palette.info.contrastText}
          >
            {alertText}
          </Typography>
        </Alert>
      )}
      <SummaryTitle
        title={t('Course overview')}
        itemId={mindItemId}
        itemData={courseMindItem}
        type='course'
      />
      <SummaryContent
        isLoading={isLoading}
        itemData={courseMindItem}
        primaryHeaderText={courseMindItem.course ? courseTypeToPresentationNameMap[courseMindItem.course.type] : ''}
        alignDetailsToBottom
      >
        <Box
          sx={{
            justifyContent: 'space-between',
            display: 'flex',
            width: '100%',
            flexDirection: isDesktopScreen ? 'row' : 'column',
            marginTop: isDesktopScreen ? undefined: spaces.xs
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: spaces.md,
              marginBottom: isDesktopScreen ? undefined : spaces.md,
              paddingRight: isDesktopScreen ? spaces.md : 0,
            }}>
            {courseMindItem.course && (
              <Metadata 
                icon='clock'
                label={`${courseMindItem.course.duration} ${t('Minutes')}`}
              />
            )}
            {courseMindItem.course && (
              <Tooltip label={courseMindItem.course.theme.join(', ')} placement='top' hideWithoutOverflow>
                <Metadata
                  icon='tag'
                  label={courseMindItem.course.theme.join(', ')}
                />
              </Tooltip>
            )}
          </Box>
          <CourseCoverButton coverPageSection='summary' />
        </Box>
      </SummaryContent>
    </SummaryContainer>
  );
};

export default CourseCoverPageSummary;
